import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

const SprintCentralTeam = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projId } = useParams();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projId || "");

  const [teamMembers, setTeamMembers] = useState([]);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProjectId]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const initialForm = {
    name: "",
    role: "Developer",
    email: "",
    capacity: "100%",
    status: "Active",
  };

  const [formData, setFormData] = useState(initialForm);

  // ---------------- SAMPLE DATA ----------------

  const demoProjects = [
    { id: 1, name: "Website Redesign 2026" },
    { id: 2, name: "ERP Integration Phase 1" },
    { id: 3, name: "Customer Portal" },
  ];

  const demoMembers = [
    {
      id: 1,
      projectId: 1,
      name: "Rahul Sharma",
      role: "Backend Developer",
      email: "rahul@example.com",
      capacity: "100%",
      status: "Active",
    },
    {
      id: 2,
      projectId: 1,
      name: "Priya Patel",
      role: "Frontend Developer",
      email: "priya@example.com",
      capacity: "80%",
      status: "Active",
    },
    {
      id: 3,
      projectId: 2,
      name: "Amit Verma",
      role: "QA Engineer",
      email: "amit@example.com",
      capacity: "100%",
      status: "Active",
    },
  ];

  const loadSample = () => {
    setProjects(demoProjects);
    setTeamMembers(demoMembers);
  };

  // ---------------- CRUD ----------------

  const handleAdd = () => {
    if (!selectedProjectId) {
      alert("Please select a project first.");
      return;
    }

    setEditingMember(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (member) => {
    setEditingMember(member);

    setFormData({
      name: member.name,
      role: member.role,
      email: member.email,
      capacity: member.capacity,
      status: member.status,
    });

    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this team member?")) return;

    setTeamMembers((prev) => prev.filter((m) => m.id !== id));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email) {
      alert("Name and Email required.");
      return;
    }

    const newMember = {
      id: editingMember?.id || Date.now(),
      projectId: Number(selectedProjectId),
      ...formData,
    };

    if (editingMember) {
      setTeamMembers((prev) =>
        prev.map((m) => (m.id === editingMember.id ? newMember : m)),
      );
    } else {
      setTeamMembers((prev) => [...prev, newMember]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // ---------------- FILTER ----------------

  const projectMembers = teamMembers.filter(
    (m) => String(m.projectId) === String(selectedProjectId),
  );

  const filtered = projectMembers.filter(
    (m) =>
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.role.toLowerCase().includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filtered.length / perPage);

  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage,
  );

  const roleColor = {
    "Backend Developer": "bg-green-600",
    "Frontend Developer": "bg-cyan-600",
    "QA Engineer": "bg-yellow-600",
    "Scrum Master": "bg-purple-600",
    "Product Owner": "bg-red-600",
    Developer: "bg-blue-600",
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-2">
      {/* HEADER */}

      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            👥 SprintCentral Team
          </div>
          <div className="text-xs text-gray-400">
            Agile Team Member Management
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>

              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 rounded-md"
            >
              🔐
            </button>
          )}
        </div>
      </header>

      <div className="mt-4 min-h-[75vh]">
        {/* ACTION BAR */}

        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <button
            onClick={loadSample}
            className="text-xs lg:text-sm w-40 lg:w-64 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> Sample Team
          </button>

          <button
            onClick={handleAdd}
            className="text-xs lg:text-sm w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> New Member
          </button>

          {!projId && (
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg h-12 px-3 py-2"
            >
              <option value="">Select Project</option>

              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}

          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />

            <input
              type="text"
              placeholder="Search team..."
              value={search}
              style={{ width: "100%" }}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg"
            />
          </div>
        </div>
{/* MODAL */}

      {showModal && (
        <div className="bg-black w-full rounded-lg p-4 border border-cyan-200 shadow-xl mt-2">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg text-cyan-200 font-semibold">
              {editingMember ? "Edit Team Member" : "New Team Member"}
            </h2>

            <button
              className="w-12 bg-blue-500"
              onClick={() => setShowModal(false)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              type="text"
              placeholder="Name"
              required
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full bg-gray-900 border border-gray-700 rounded p-2"
            />

            <input
              type="email"
              placeholder="Email"
              required
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className="w-full bg-gray-900 border border-gray-700 rounded p-2"
            />

            <input
              type="text"
              placeholder="Role"
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
              className="w-full bg-gray-900 border border-gray-700 rounded p-2"
            />

            <input
              type="text"
              placeholder="Capacity"
              value={formData.capacity}
              onChange={(e) =>
                setFormData({ ...formData, capacity: e.target.value })
              }
              className="w-40 bg-gray-900 border border-gray-700 rounded p-2"
            />

            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
              className="w-40 bg-gray-900 border border-gray-700 rounded p-2"
            >
              <option>Active</option>
              <option>On Leave</option>
              <option>Inactive</option>
            </select>

            <button
              type="submit"
              className="ml-4 w-40 bg-cyan-600 hover:bg-cyan-500 rounded p-2"
            >
              {editingMember ? "Update Member" : "Create Member"}
            </button>
          </form>
        </div>
      )}        

        {/* TEAM CARDS */}

        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-4">
          {paginated.map((m) => (
            <div
              key={m.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5"
            >
              <div className="flex justify-between items-start">
                <h3 className="font-semibold text-white">{m.name}</h3>

                <span
                  className={`px-2 py-1 rounded text-xs ${roleColor[m.role]}`}
                >
                  {m.role}
                </span>
              </div>

              <div className="text-xs text-gray-400 mt-2">Email: {m.email}</div>

              <div className="text-xs text-gray-400">
                Capacity: {m.capacity}
              </div>

              <div className="text-xs text-gray-400">Status: {m.status}</div>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleEdit(m)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>

                <button
                  onClick={() => handleDelete(m.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FOOTER */}

      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-6">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">
              About
            </Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">
              Terms of Use
            </Link>
            <Link to="/contact-us" className="hover:text-teal-300">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SprintCentralTeam;
